/** @module @liquidefy/code-editor/git/git-hooks @version 1.0.0 @license LQD Research LLC */

import type {
  EditorResult,
  GitHookName,
  GitHookState,
  GitProviderType,
  GitProviderConfig,
  PullRequestState,
  PRReviewer,
  PRCheck,
  HookId,
  BranchName,
  CommitHash,
  GitCommit,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asHookId,
  asBranchName,
  now,
} from '../../code-editor.js';

// ═══ §1 IMPORTS — re-exported above ═══

// ═══ §2 HOOK MANAGEMENT — create, enable, disable, query hooks ═══

export function createHook(name: GitHookName, path?: string): EditorResult<GitHookState> {
  const hook: GitHookState = {
    id: asHookId(generateId('hook')),
    name,
    path: path ?? `.git/hooks/${name}`,
    isEnabled: true,
    isExecutable: true,
  };
  return ok(hook, 'HOOK_CREATED', `Created ${name} hook`);
}

export function enableHook(state: GitHookState): EditorResult<GitHookState> {
  if (state.isEnabled) return ok(state, 'HOOK_ALREADY_ENABLED', `Hook ${state.name} is already enabled`);
  return ok({ ...state, isEnabled: true }, 'HOOK_ENABLED', `Enabled ${state.name} hook`);
}

export function disableHook(state: GitHookState): EditorResult<GitHookState> {
  if (!state.isEnabled) return ok(state, 'HOOK_ALREADY_DISABLED', `Hook ${state.name} is already disabled`);
  return ok({ ...state, isEnabled: false }, 'HOOK_DISABLED', `Disabled ${state.name} hook`);
}

export function isHookEnabled(state: GitHookState): boolean {
  return state.isEnabled;
}

export function isHookExecutable(state: GitHookState): boolean {
  return state.isExecutable;
}

export function hookFilePath(rootPath: string, name: GitHookName): string {
  return `${rootPath}/.git/hooks/${name}`;
}

export function allHooks(): GitHookName[] {
  return [
    'pre-commit',
    'prepare-commit-msg',
    'commit-msg',
    'post-commit',
    'pre-rebase',
    'post-rewrite',
    'post-checkout',
    'post-merge',
    'pre-push',
    'pre-auto-gc',
  ];
}

export const HOOK_DESCRIPTIONS: Record<GitHookName, string> = {
  'pre-commit': 'Runs before a commit is created. Used for linting and tests.',
  'prepare-commit-msg': 'Runs after the default message is created but before the editor opens.',
  'commit-msg': 'Runs after the commit message is entered. Used to validate message format.',
  'post-commit': 'Runs after a commit is created. Used for notifications.',
  'pre-rebase': 'Runs before a rebase starts. Can prevent rebase.',
  'post-rewrite': 'Runs after commands that rewrite commits (amend, rebase).',
  'post-checkout': 'Runs after a checkout. Used for environment setup.',
  'post-merge': 'Runs after a merge. Used for dependency installation.',
  'pre-push': 'Runs before a push. Used for final validation.',
  'pre-auto-gc': 'Runs before automatic garbage collection.',
};

export const GIT_HOOK_ORDER: GitHookName[] = [
  'pre-commit',
  'prepare-commit-msg',
  'commit-msg',
  'post-commit',
  'pre-rebase',
  'post-rewrite',
  'post-checkout',
  'post-merge',
  'pre-push',
  'pre-auto-gc',
];

export function hookExecutionOrder(hooks: GitHookState[]): GitHookState[] {
  const orderMap = new Map(GIT_HOOK_ORDER.map((name, idx) => [name, idx]));
  return [...hooks].sort((a, b) => {
    const orderA = orderMap.get(a.name) ?? 999;
    const orderB = orderMap.get(b.name) ?? 999;
    return orderA - orderB;
  });
}

// ═══ §3 HOOK TEMPLATES — shell script templates for common hooks ═══

export function defaultHookContent(name: GitHookName): string {
  return `#!/bin/sh
# ${name} hook
# ${HOOK_DESCRIPTIONS[name]}

# Exit with non-zero to abort the operation
exit 0
`;
}

export function preCommitLintTemplate(lintCommand: string): string {
  return `#!/bin/sh
# pre-commit hook — run linter on staged files

echo "Running lint..."
${lintCommand}
RESULT=$?

if [ $RESULT -ne 0 ]; then
  echo "Lint failed. Commit aborted."
  exit 1
fi

exit 0
`;
}

export function commitMsgConventionalTemplate(): string {
  return `#!/bin/sh
# commit-msg hook — enforce conventional commit format

MSG_FILE=$1
MSG=$(cat "$MSG_FILE")

PATTERN='^(feat|fix|docs|style|refactor|perf|test|build|ci|chore|revert|wip)(\\(.+\\))?(!)?: .{1,}'

if ! echo "$MSG" | grep -qE "$PATTERN"; then
  echo "ERROR: Commit message does not follow Conventional Commits format."
  echo ""
  echo "Expected format: type(scope): description"
  echo "  Types: feat, fix, docs, style, refactor, perf, test, build, ci, chore, revert, wip"
  echo ""
  echo "Example: feat(auth): add OAuth2 login flow"
  echo ""
  exit 1
fi

# Check subject line length
SUBJECT=$(echo "$MSG" | head -1)
if [ ${'$'}{#SUBJECT} -gt 72 ]; then
  echo "ERROR: Subject line exceeds 72 characters (${'$'}{#SUBJECT} chars)."
  exit 1
fi

exit 0
`;
}

export function prePushProtectedBranchTemplate(branches: string[]): string {
  const branchChecks = branches.map((b) => `    "${b}")`).join('\n');
  const branchList = branches.join(', ');
  return `#!/bin/sh
# pre-push hook — prevent direct pushes to protected branches

CURRENT_BRANCH=$(git symbolic-ref HEAD 2>/dev/null | sed 's|refs/heads/||')

case "$CURRENT_BRANCH" in
${branches.map((b) => `  "${b}")\n    echo "ERROR: Direct push to ${b} is not allowed."\n    echo "Please create a pull request instead."\n    exit 1\n    ;;`).join('\n')}
  *)
    ;;
esac

exit 0
`;
}

export function hookShebang(shell: string): string {
  return `#!/usr/bin/env ${shell}`;
}

export function makeExecutable(hookContent: string): string {
  if (hookContent.startsWith('#!')) return hookContent;
  return '#!/bin/sh\n' + hookContent;
}

// ═══ §4 PROVIDER CONFIG — git hosting provider configuration ═══

export const PROVIDER_API_URLS: Record<GitProviderType, string> = {
  github: 'https://api.github.com',
  gitlab: 'https://gitlab.com/api/v4',
  gitea: 'https://gitea.com/api/v1',
  bitbucket: 'https://api.bitbucket.org/2.0',
  local: '',
};

export function createProviderConfig(
  type: GitProviderType,
  apiUrl: string,
  token?: string
): EditorResult<GitProviderConfig> {
  const config: GitProviderConfig = {
    type,
    apiUrl: apiUrl || PROVIDER_API_URLS[type],
    token,
    username: undefined,
  };
  return ok(config, 'PROVIDER_CONFIGURED', `Configured ${type} provider`);
}

export function providerApiBase(config: GitProviderConfig): string {
  return config.apiUrl || PROVIDER_API_URLS[config.type];
}

export function isProviderConfigured(config: GitProviderConfig): boolean {
  if (config.type === 'local') return true;
  return config.apiUrl.length > 0 && config.token !== undefined && config.token.length > 0;
}

export function providerDisplayName(type: GitProviderType): string {
  switch (type) {
    case 'github': return 'GitHub';
    case 'gitlab': return 'GitLab';
    case 'gitea': return 'Gitea';
    case 'bitbucket': return 'Bitbucket';
    case 'local': return 'Local';
  }
}

export function providerDefaultApiUrl(type: GitProviderType): string {
  return PROVIDER_API_URLS[type];
}

// ═══ §5 PR MANAGEMENT — create, update, review pull requests ═══

export function createPR(
  title: string,
  body: string,
  source: BranchName,
  target: BranchName,
  options?: { author?: string; labels?: string[]; reviewers?: string[] }
): EditorResult<PullRequestState> {
  if (!title.trim()) return err('EMPTY_TITLE', 'PR title cannot be empty');
  const timestamp = now();
  const pr: PullRequestState = {
    id: generateId('pr'),
    number: 0,
    title: title.trim(),
    body,
    state: 'open',
    author: options?.author ?? 'unknown',
    sourceBranch: source,
    targetBranch: target,
    reviewers: (options?.reviewers ?? []).map((name) => ({ name, state: 'pending' as const })),
    checks: [],
    labels: options?.labels ?? [],
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  return ok(pr, 'PR_CREATED', `Created PR: "${title}"`);
}

export function updatePRState(
  pr: PullRequestState,
  state: 'open' | 'closed' | 'merged'
): EditorResult<PullRequestState> {
  return ok(
    { ...pr, state, updatedAt: now() },
    'PR_STATE_UPDATED',
    `Updated PR #${pr.number} state to ${state}`
  );
}

export function addReviewer(
  pr: PullRequestState,
  name: string
): EditorResult<PullRequestState> {
  const exists = pr.reviewers.some((r) => r.name === name);
  if (exists) return err('REVIEWER_EXISTS', `Reviewer "${name}" already added`);
  const reviewer: PRReviewer = { name, state: 'pending' };
  return ok(
    { ...pr, reviewers: [...pr.reviewers, reviewer], updatedAt: now() },
    'REVIEWER_ADDED',
    `Added reviewer "${name}" to PR #${pr.number}`
  );
}

export function updateReviewerState(
  pr: PullRequestState,
  name: string,
  state: PRReviewer['state']
): EditorResult<PullRequestState> {
  const idx = pr.reviewers.findIndex((r) => r.name === name);
  if (idx === -1) return err('REVIEWER_NOT_FOUND', `Reviewer "${name}" not found`);
  const updated: PRReviewer = { ...pr.reviewers[idx]!, state };
  const reviewers = [...pr.reviewers.slice(0, idx), updated, ...pr.reviewers.slice(idx + 1)];
  return ok(
    { ...pr, reviewers, updatedAt: now() },
    'REVIEWER_STATE_UPDATED',
    `${name} ${state} PR #${pr.number}`
  );
}

export function addLabel(
  pr: PullRequestState,
  label: string
): EditorResult<PullRequestState> {
  if (pr.labels.includes(label)) return ok(pr, 'LABEL_EXISTS', `Label "${label}" already on PR`);
  return ok(
    { ...pr, labels: [...pr.labels, label], updatedAt: now() },
    'LABEL_ADDED',
    `Added label "${label}" to PR #${pr.number}`
  );
}

export function removeLabel(
  pr: PullRequestState,
  label: string
): EditorResult<PullRequestState> {
  const filtered = pr.labels.filter((l) => l !== label);
  if (filtered.length === pr.labels.length) return err('LABEL_NOT_FOUND', `Label "${label}" not found`);
  return ok(
    { ...pr, labels: filtered, updatedAt: now() },
    'LABEL_REMOVED',
    `Removed label "${label}" from PR #${pr.number}`
  );
}

export function addCheck(
  pr: PullRequestState,
  check: PRCheck
): EditorResult<PullRequestState> {
  const exists = pr.checks.some((c) => c.name === check.name);
  if (exists) {
    const checks = pr.checks.map((c) => c.name === check.name ? check : c);
    return ok({ ...pr, checks, updatedAt: now() }, 'CHECK_UPDATED', `Updated check "${check.name}"`);
  }
  return ok(
    { ...pr, checks: [...pr.checks, check], updatedAt: now() },
    'CHECK_ADDED',
    `Added check "${check.name}" to PR #${pr.number}`
  );
}

export function updateCheckStatus(
  pr: PullRequestState,
  checkName: string,
  status: PRCheck['status'],
  conclusion?: PRCheck['conclusion']
): EditorResult<PullRequestState> {
  const idx = pr.checks.findIndex((c) => c.name === checkName);
  if (idx === -1) return err('CHECK_NOT_FOUND', `Check "${checkName}" not found`);
  const updated: PRCheck = { ...pr.checks[idx]!, status, conclusion };
  const checks = [...pr.checks.slice(0, idx), updated, ...pr.checks.slice(idx + 1)];
  return ok(
    { ...pr, checks, updatedAt: now() },
    'CHECK_STATUS_UPDATED',
    `${checkName}: ${status}${conclusion ? ` (${conclusion})` : ''}`
  );
}

export function isPRMergeable(pr: PullRequestState): boolean {
  if (pr.state !== 'open') return false;
  const allChecksPass = pr.checks.every(
    (c) => c.status === 'completed' && (c.conclusion === 'success' || c.conclusion === 'neutral')
  );
  const hasApproval = pr.reviewers.some((r) => r.state === 'approved');
  const noChangesRequested = !pr.reviewers.some((r) => r.state === 'changes-requested');
  return allChecksPass && hasApproval && noChangesRequested;
}

export function prAge(pr: PullRequestState, nowMs: number): number {
  return Math.max(0, nowMs - pr.createdAt);
}

export function prSummary(pr: PullRequestState): string {
  const checksStatus = pr.checks.length > 0
    ? `${pr.checks.filter((c) => c.conclusion === 'success').length}/${pr.checks.length} checks`
    : 'no checks';
  const reviewStatus = pr.reviewers.length > 0
    ? `${pr.reviewers.filter((r) => r.state === 'approved').length}/${pr.reviewers.length} approvals`
    : 'no reviewers';
  return `#${pr.number} ${pr.title} [${pr.state}] (${checksStatus}, ${reviewStatus})`;
}

// ═══ §6 PR ANALYSIS — review progress, checks, templates, git commands ═══

export function reviewProgress(
  pr: PullRequestState
): { approved: number; pending: number; changesRequested: number } {
  let approved = 0;
  let pending = 0;
  let changesRequested = 0;
  for (const r of pr.reviewers) {
    switch (r.state) {
      case 'approved': approved++; break;
      case 'pending': pending++; break;
      case 'changes-requested': changesRequested++; break;
    }
  }
  return { approved, pending, changesRequested };
}

export function allChecksPass(pr: PullRequestState): boolean {
  if (pr.checks.length === 0) return true;
  return pr.checks.every(
    (c) => c.status === 'completed' && (c.conclusion === 'success' || c.conclusion === 'neutral')
  );
}

export function failedChecks(pr: PullRequestState): PRCheck[] {
  return pr.checks.filter(
    (c) => c.status === 'completed' && c.conclusion !== 'success' && c.conclusion !== 'neutral'
  );
}

export function pendingChecks(pr: PullRequestState): PRCheck[] {
  return pr.checks.filter((c) => c.status !== 'completed');
}

export function formatPRDescription(commits: GitCommit[]): string {
  if (commits.length === 0) return '';
  const lines: string[] = ['## Changes', ''];
  for (const commit of commits) {
    const hash = (commit.hash as string).slice(0, 7);
    const firstLine = commit.message.split('\n')[0];
    lines.push(`- \`${hash}\` ${firstLine}`);
  }
  return lines.join('\n');
}

export function prTemplate(type: 'feat' | 'fix' | 'chore'): string {
  switch (type) {
    case 'feat':
      return [
        '## Summary',
        '',
        'Describe the new feature.',
        '',
        '## Motivation',
        '',
        'Why is this change needed?',
        '',
        '## Testing',
        '',
        '- [ ] Unit tests added',
        '- [ ] Integration tests pass',
        '- [ ] Manual testing done',
      ].join('\n');
    case 'fix':
      return [
        '## Bug Description',
        '',
        'What was the bug?',
        '',
        '## Root Cause',
        '',
        'What caused the bug?',
        '',
        '## Fix',
        '',
        'How was it fixed?',
        '',
        '## Testing',
        '',
        '- [ ] Regression test added',
        '- [ ] Existing tests pass',
      ].join('\n');
    case 'chore':
      return [
        '## Changes',
        '',
        'Describe the maintenance changes.',
        '',
        '## Impact',
        '',
        'What is affected by this change?',
      ].join('\n');
  }
}

export function buildGitCommand(
  command: string,
  args: string[],
  flags?: Record<string, string | boolean>
): string {
  const parts = ['git', command];
  if (flags) {
    for (const [key, value] of Object.entries(flags)) {
      if (value === true) {
        parts.push(key.length === 1 ? `-${key}` : `--${key}`);
      } else if (value !== false) {
        parts.push(key.length === 1 ? `-${key}` : `--${key}`);
        parts.push(value);
      }
    }
  }
  parts.push(...args);
  return parts.join(' ');
}
